import { ChevronDown, ChevronRight, LogOut } from "lucide-react";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { useState } from "react";
import { HiHome } from "react-icons/hi2";
import { TbLogout } from "react-icons/tb";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotifBell from "@/components/ui/notif-bell";
import { BASE_PATH } from "@/shared/constants/constant";
import { cn } from "@/shared/lib/utils";
import useStore from "@/shared/store/useStore";
import { ConfirmationDialog } from "../ui/custom/confirmationDialog";
import { SidebarTrigger, SidebarTriggerChevron } from "../ui/sidebar";
import { DASHBOARD } from "@/shared/constants/path";

export default function Header() {
	const router = useRouter();
	const {
		query: { id },
	} = router;
	const pathname = usePathname();
	const [showLogoutDialog, setShowLogoutDialog] = useState(false);
	const arrPath = (pathname || "").split("/");
	const { logout, user } = useStore();

	const HIDDEN_BREADCRUMBS = ["dashboard"];
	const filteredPath = arrPath.filter(
	(path, index) => !(HIDDEN_BREADCRUMBS.includes(path) && index !== 0),
	);

	return (
		<header className="max-h-10 h-full flex items-center justify-between ">
			<div className="flex flex-row items-center gap-6">
				<SidebarTriggerChevron className="hidden md:flex"/>
				<Breadcrumb className="text-sm">
					<BreadcrumbList>
						<div className="flex flex-row items-center gap-2">
							<BreadcrumbItem className="flex items-center">
								<HiHome className="size-4" />
								<BreadcrumbLink href={`${BASE_PATH}${DASHBOARD}`}>
									Dashboard
								</BreadcrumbLink>
							</BreadcrumbItem>
						</div>
						{filteredPath.slice(1).map((path, index) => {
							const isLast = index === filteredPath.length - 2;
							let breadcrumbItem = path.split("-").join(" ");
							const breadcrumbPath = isLast
								? `${BASE_PATH}/${filteredPath.slice(1, index + 2).join("/")}`
								: `${BASE_PATH}/${filteredPath.slice(1, index + 2).join("/")}/`;

							// handle specific cases for breadcrumb items
							if (
								typeof Number(path) === "number" &&
								!Number.isNaN(Number(path)) &&
								path !== ""
							) {
								return null;
							}

							// edge case for specific paths
							if (breadcrumbItem === "tnc") {
								breadcrumbItem = "TnC";
							}

							return (
								<div
									className="flex flex-row items-center gap-2"
									key={`breadcumb-path${breadcrumbItem}`}
								>
									<BreadcrumbSeparator>
										<ChevronRight />
									</BreadcrumbSeparator>
									<BreadcrumbItem>
										<BreadcrumbLink
											href={
												index === 0
													? undefined
													: index === 2 &&
															["update", "edit", "detail"].includes(path)
														? breadcrumbPath + id
														: breadcrumbPath
											}
											className={cn(
												"capitalize",
												index === 0 ? "cursor-default" : "cursor-pointer",
												path === filteredPath[filteredPath.length - 1] ||
													(index === 2 &&
														["update", "add", "edit", "detail"].includes(
															path,
														) &&
														filteredPath.length === 5) ||
													(index === 4 && ["product-detail"].includes(path))
													? "font-bold text-gray-500"
													: "",
											)}
										>
											{breadcrumbItem}
										</BreadcrumbLink>
									</BreadcrumbItem>
								</div>
							);
						})}
					</BreadcrumbList>
				</Breadcrumb>
			</div>
			<div className="flex gap-5 space-x-5 items-center">
				<NotifBell />
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<div className="group cursor-pointer flex items-center gap-1 hover:bg-gray-200 rounded-full pr-3">
							<Avatar className="border border-gray-300">
								<AvatarImage src="" />
								<AvatarFallback>
									{user?.user_name
										? user?.user_name.slice(0, 2).toUpperCase()
										: ""}
								</AvatarFallback>
							</Avatar>
							<ChevronDown
								size={16}
								className="group-hover:rotate-180 group-focus:rotate-180 text-gray-600 duration-300 transition-all"
							/>
						</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent className="w-5" align="end" sideOffset={10}>
						<DropdownMenuItem
							className="group cursor-pointer p-1"
							onClick={() => setShowLogoutDialog(true)}
						>
							<div className="flex text-gray-600 group-hover:text-gray-800 duration-300 transition items-center justify-between w-full">
								<span className="font-semibold text-base group-hover:translate-x-2 duration-300 transition">
									Logout
								</span>{" "}
								<LogOut className="group-hover:-rotate-90 group-hover:-translate-x-2 duration-300 transition" />
							</div>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<SidebarTrigger className="block md:hidden" />
			</div>
			<ConfirmationDialog
				open={showLogoutDialog}
				icon={<TbLogout className="text-4xl text-destructive" />}
				title="Are you sure you want to log out?"
				description="You will be logged out of your account and redirected to the login page."
				buttonConfirmVariant="destructive"
				buttonConfirmText="Yes, Log Out"
				onConfirm={(isConfirmed) => {
					isConfirmed && logout();
					setShowLogoutDialog(false);
				}}
			/>
		</header>
	);
}
